import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DialogMessageService } from '../../services/dialog-message.service';
import { MsalHelperService } from '../../services/msal-helper.service';
import { OrgManagementService } from '../../services/org-management.service';
import { QuarterSelectorService } from '../../services/quarter-selector.service';
import { UserService } from '../../services/user.service';
import { Organization } from '../../types/organization';
import { OQContext } from '../../types/organization-quarter-context';
import { Quarter } from '../../types/quarter';
import { User } from '../../types/user';

@Component({
    selector: 'org-quarter-selector',
    templateUrl: './org-quarter-selector.component.html',
    styleUrls: ['./org-quarter-selector.component.scss'],
    standalone: false
})
export class OrgQuarterSelectorComponent implements OnInit, OnDestroy {
  @Input() user: User;
  organizations: Organization[];
  quarters: Quarter[];
  selectedOQContext: OQContext = { org: null, qtr: null };
  newOrgSubscription: Subscription;
  @ViewChild('orgSelector') orgSelector: MatSelect;

  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly msalHelperService: MsalHelperService,
    private readonly quarterSelectorService: QuarterSelectorService,
    private readonly messageService: DialogMessageService,
    private readonly orgManager: OrgManagementService,
    private readonly route: ActivatedRoute,
  ) { }

  ngOnInit(): void {
    this.initializeOrgData();
  }

  ngOnDestroy(): void {
    this.newOrgSubscription.unsubscribe();
  }

  initializeOrgData() {
    this.msalHelperService.runAfterAuthenticated(() => {
      this.userService.currentUser.subscribe({
        next: (result) => {
          this.user = result;
          this.organizations = result.organizations.sort(this.sortOrg);
          this.chooseSelectedOrg();
          this.route.queryParams.subscribe({
            next: (params) => {
              if (params.org) {
                this.setSelectedOrgById(parseInt(params.org));
              }
            }
          });
        },
        error: (err) => {
          this.messageService.openErrorMessage(err);
        }
      });
    });
  }

  selectedOrgChanged(org: Organization) {
    this.quarters = org.availableQuarters.sort(this.sortQtr);
    this.selectedOQContext.org = org;
    localStorage.setItem('lastSelectedOrg', `${org.id}`);
    this.selectActiveQuarter(null);
    this.quarterSelectorService.setSelectedContext(this.selectedOQContext);
  }

  selectedQuarterChanged(quarter: Quarter) {
    this.selectActiveQuarter(quarter);
  }

  setSelectedOrgById(id: number) {
    for (const i of this.organizations) {
      if (i.id === id) {
        this.selectedOrgChanged(i);
        return;
      }
    }
  }

  chooseSelectedOrg() {
    // If URI has org, use it
    // else if localstorage is set, use it
    // else use the first one
    const last = localStorage.getItem('lastSelectedOrg');
    if (last) {
      const selectedId = parseInt(last);
      for (const _org of this.organizations) {
        if (_org.id === selectedId) {
          this.selectedOQContext.org = _org;
          this.quarters = this.selectedOQContext.org.availableQuarters.sort(this.sortQtr);
          this.selectActiveQuarter(null);
          this.quarterSelectorService.setSelectedContext(this.selectedOQContext);
          return;
        }
      }
    }
    this.selectedOQContext.org = this.organizations[0];
    this.quarters = this.selectedOQContext.org.availableQuarters.sort(this.sortQtr);
    this.selectActiveQuarter(null);
    this.quarterSelectorService.setSelectedContext(this.selectedOQContext);
  }

  selectActiveQuarter(quarter: Quarter) {
    if (quarter == null) {
      for (const q of this.quarters) {
        if (q.status === 'Active') {
          this.selectedOQContext.qtr = q;
          return;
        }
      }
    }
    this.selectedOQContext.qtr = quarter;
    this.quarterSelectorService.setSelectedContext(this.selectedOQContext);
  }

  sortOrg(a: Organization, b: Organization) {
    if (a.orgCode < b.orgCode) {
      return -1;
    }
    else if (a.orgCode > b.orgCode) {
      return 1;
    }
    return 0;
  }

  sortQtr(a: Quarter, b: Quarter) {
    return a.id - b.id;
  }

  isAdminRoute(): boolean {
    return this.router.url.startsWith('/admin');
  }

  isUserActive(): boolean {
    return this.user.organizations.length > 0;
  }

  //when a user opens the org selector, this function will ensure their currently selected org is visible in the view.
  setViewToSelected() {
    this.orgSelector.panel.nativeElement.querySelector('.mdc-list-item--selected').focus();
  }
}
